import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { TableList, TableListProvider, Typography } from 'seagull';
import { AddServicesButton } from '~/components/AddServicesButton/AddServicesButton';
import { TemplateVariablesProvider } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariablesContext';
import { useLocaleKeys } from '~/i18n';
import { AgreementDataModel } from '~/models';
import { getServicesTableDefs } from '../EditDraftsTable.utils';
import EditPackageNameButton from '../serviceCells/EditPackageNameButton';
import styles from './EditDraftService.module.scss';
import EditDraftServiceRow from './EditDraftServiceRow';
const getServicesByBundle = (draft)=>{
    const services = draft.services();
    const standaloneServices = services.length > 0 ? [
        {
            packageName: null,
            services,
            packageId: null
        }
    ] : [];
    const bundledServices = draft.serviceBundles().map((bundle)=>({
            packageName: bundle.name,
            packageId: bundle.id,
            services: bundle.services
        }));
    return [
        ...standaloneServices,
        ...bundledServices
    ];
};
export default function EditDraftServiceTable(param) {
    let { draft, onOpenModal, onAddServices, contact, worksAt } = param;
    const { t: draftTexts } = useLocaleKeys('proposal.draftsDashboard');
    const servicesTableDefs = getServicesTableDefs();
    const servicesByBundle = getServicesByBundle(draft);
    const isDisabled = draft.isScheduled();
    const disabledTooltip = isDisabled ? 'In order to edit a scheduled proposal, its scheduling must be canceled.' : undefined;
    const textFields = useMemo(()=>{
        const services = draft.services();
        const bundles = draft.serviceBundles();
        return [
            draft.title(),
            draft.customNotificationText(),
            ...services.map((s)=>s.name),
            ...bundles.map((b)=>b.name),
            ...bundles.flatMap((b)=>b.services.map((s)=>s.name))
        ];
    }, [
        draft
    ]);
    const richTextFields = useMemo(()=>{
        const services = draft.services();
        const bundles = draft.serviceBundles();
        return [
            draft.extraClause(),
            ...services.map((s)=>s.description),
            ...services.map((s)=>s.terms),
            ...bundles.flatMap((b)=>b.services.map((s)=>s.description)),
            ...bundles.flatMap((b)=>b.services.map((s)=>s.terms))
        ];
    }, [
        draft
    ]);
    function renderAddServicesButton(param) {
        let { packageId } = param;
        return /*#__PURE__*/ _jsx(AddServicesButton, {
            className: styles.addServicesButton,
            variant: "textWithIcon",
            popoverHorizontalAlign: "left",
            agreementData: AgreementDataModel.fromDraft(draft),
            disabled: isDisabled,
            testId: "add-services-".concat(packageId || draft.id()),
            onServicesAdded: (services)=>onAddServices(services, packageId)
        });
    }
    return /*#__PURE__*/ _jsx(TemplateVariablesProvider, {
        contact: contact,
        worksAt: worksAt,
        agreementName: draft.title(),
        effectiveDate: draft.effectiveDateAsDate(),
        initialTemplateVariableValues: draft.templateVariableValues(),
        textFields: textFields,
        richTextFields: richTextFields,
        children: /*#__PURE__*/ _jsx("div", {
            children: servicesByBundle.length > 0 ? servicesByBundle.map((param, bundleIndex)=>{
                let { packageName, packageId, services } = param;
                return /*#__PURE__*/ _jsxs("div", {
                    className: styles.bundleTable,
                    children: [
                        packageId && /*#__PURE__*/ _jsxs("div", {
                            className: styles.bundleHeader,
                            "data-testid": "bundle-header-".concat(packageId),
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    testId: "package-name-".concat(packageId),
                                    children: packageName
                                }),
                                /*#__PURE__*/ _jsx(EditPackageNameButton, {
                                    packageName: packageName,
                                    draft: draft,
                                    packageId: packageId,
                                    onOpenModal: onOpenModal
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(TableListProvider, {
                            columnDefs: servicesTableDefs,
                            hasActionColumn: true,
                            children: [
                                /*#__PURE__*/ _jsx(TableList.Header, {
                                    items: servicesTableDefs
                                }),
                                services.length > 0 && services.map((service, index)=>/*#__PURE__*/ _jsx(EditDraftServiceRow, {
                                        service: service,
                                        packageId: packageId,
                                        index: index,
                                        draft: draft,
                                        onOpenModal: onOpenModal,
                                        isDisabled: isDisabled,
                                        disabledTooltip: disabledTooltip,
                                        contact: contact,
                                        worksAt: worksAt
                                    }, service.id)),
                                services.length === 0 && packageId && /*#__PURE__*/ _jsx("div", {
                                    className: styles.noServices,
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        children: draftTexts.noServicesInPackage()
                                    })
                                }),
                                renderAddServicesButton({
                                    packageId
                                })
                            ]
                        })
                    ]
                }, packageId || "standalone-".concat(bundleIndex));
            }) : renderAddServicesButton({
                packageId: null
            })
        })
    });
}
